<template>
  <FlexCol gap-3 class="h-full min-h-0">
    <Title :title="title" :breadcrumb="true">
      <Button label="Voltar" icon="fa-solid fa-arrow-left" primary :disabled="!canBack" @click="goBack" />
      <slot name="titleButtons"></slot>
    </Title>
    <slot name="middleCard"></slot>
    <TheLoader v-if="loader" />
    <Card>
      <Accordion v-for="section in form" :key="section.section" :title="section.section" v-show="section.show === undefined ? true : !!section.show">
        <slot :name="`section-${section.id}`">
          <FlexCol>
            <Grid gap-3 :cols-4="!section.cols" :class="section.cols">
              <FormField
                v-for="field in visibleFields(section.fields)"
                :key="field.name"
                :class="field.class || field.className"
                :style="field.style"
                v-model="object[field.name]"
                :label="field.label"
                :status="field.status"
                :type="field.type"
                :placeholder="field.placeholder"
                :options="field.options"
                :items="field.items"
                :database="field.database"
                :item-value="field.itemValue"
                :item-text="field.itemText"
                :item-autocomplete="field.itemAutocomplete"
                :table-filter="field.tableFilter"
                :clearable="field.clearable"
                :disabled="field.disabled"
                :required="field.required"
                :mask="field.mask"
                :error="errors[field.name]" />
            </Grid>
          </FlexCol>
        </slot>
      </Accordion>

      <slot name="footer" v-bind="{ save, loader, object, errors }">
        <TheRequiredLabel class="py-3" />
        <Flex gap-2>
          <Button label="Salvar" icon="fa-solid fa-floppy-disk" primary @click="save" />
          <Button label="Cancelar" icon="fa-solid fa-x" danger :disabled="!canBack" @click="goBack" id="backBtn" />
        </Flex>
      </slot>
    </Card>
  </FlexCol>
</template>

<script setup>
import { computed, defineEmits, defineProps, onMounted, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { insert, get, update } from '@/crud'

const props = defineProps({
  api: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    default: null,
  },
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  errors: {
    type: Object,
    default: () => ({}),
  },
  term: {
    type: String,
    default: null,
  },
  form: {
    type: Array,
    default: () => [],
  },
  idParam: {
    type: String,
    default: 'id',
  },
  idFilterColumn: {
    type: String,
    default: 'id',
  },
  updateIdField: {
    type: String,
    default: 'id',
  },
  fixedTitle: {
    type: Boolean,
    default: false,
  },
  disableBackWithoutHistory: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'update:errors'])

const router = useRouter()
const routeInfo = useRoute()

const route = ref(props.api)
const object = ref(props.modelValue)
const title = ref(props.title)
const errors = ref(props.errors)
const loader = ref(false)
const hasHistory = ref(true)
const canBack = computed(() => !props.disableBackWithoutHistory || hasHistory.value)

const goBack = () => {
  if (!canBack.value) return
  router.back()
}

const getAllowedFieldNames = () => {
  const sections = Array.isArray(props.form) ? props.form : []
  const fieldNames = new Set()

  sections.forEach((section) => {
    const fields = Array.isArray(section?.fields) ? section.fields : []
    fields.forEach((field) => {
      if (field?.name) {
        fieldNames.add(field.name)
      }
    })
  })

  return [...fieldNames]
}

const buildPayload = () => {
  const source = object.value || {}
  const allowedFieldNames = getAllowedFieldNames()

  if (!allowedFieldNames.length) {
    return { ...source }
  }

  return allowedFieldNames.reduce((payload, fieldName) => {
    if (Object.prototype.hasOwnProperty.call(source, fieldName)) {
      payload[fieldName] = source[fieldName]
    }
    return payload
  }, {})
}

const loadItem = async (id) => {
  loader.value = true
  const query = {
    params: {
      filters: [{ column: props.idFilterColumn, value: id }],
    },
  }
  const result = await get(route.value, query)
  object.value = result.data[0]
  loader.value = false
}

const save = async () => {
  loader.value = true
  let response
  const routeParamId = routeInfo.params[props.idParam]
  const payload = buildPayload()

  if (routeParamId) {
    const updateId = object.value?.[props.updateIdField] ?? routeParamId
    response = await update(route.value, updateId, payload, props.term)
  } else {
    response = await insert(route.value, payload, props.term)
  }

  loader.value = false
  errors.value = response.error ? response.data : {}

  if (!response.error) {
    router.back()
  }
}

const visibleFields = (fields = []) => fields.filter((field) => (field.show === undefined ? true : !!field.show))

watch(
  () => props.modelValue,
  (value) => {
    object.value = value || {}
  },
  { deep: true },
)

watch(
  () => props.errors,
  (value) => {
    errors.value = value || {}
  },
  { deep: true },
)

watch(
  object,
  (value) => {
    emit('update:modelValue', value)
  },
  { deep: true },
)

watch(
  errors,
  (value) => {
    emit('update:errors', value)
  },
  { deep: true },
)

onMounted(async () => {
  const historyLength = Number(window?.history?.length ?? 0)
  hasHistory.value = historyLength > 1

  const id = routeInfo.params[props.idParam]
  if (id) {
    await loadItem(id)
  }

  title.value = props.fixedTitle ? props.title : id ? `Edição de ${props.title}` : `Cadastro de ${props.title}`
})
</script>
